import {useRef, useEffect, useState, useCallback, useMemo, memo} from "react";
import { useEvent } from "react-use-event-hook";
import { createContext, useContext, useContextSelector } from "use-context-selector";
import {useImmer} from "use-immer";
import {Button} from '@/form/FormComponents';
import { useBlocker } from '@/systems/Blocker';
import { useModal, Modal, ModalContent, ModalButtons } from '@/components/Modal';
import { compareObjects } from '@/shared/utils';

const FormDataContext = createContext();
const FormCallbacksContext = createContext();
const FormDisabledContext = createContext();
const FormGroupContext = createContext();
const FormSubmitContext = createContext();
const FormGlobalContext = createContext();
const FormFilesContext = createContext();

const useFormDisabledContext = () => useContext(FormDisabledContext);
const useFormGroupContext = () => useContext(FormGroupContext);
const useFormSubmitContext = () => useContext(FormSubmitContext);
const useFormGlobalContext = () => useContext(FormGlobalContext);
const useFormFilesContext = () => useContext(FormFilesContext);

export function FormProvider({ initial_data: initial_data_prop, initial_error, onSubmit, onValidateFailed, disabled = false, readOnly = false, obviated = false, unsaved_modal, handleAutosave, autosave_enabled = false, presentation_mode = false, has_changed: has_changed_prop, files = [], children }) {

  const form_fields = useRef([]);
  const ref = useRef();
  const [initial_data, setInitialData] = useState(initial_data_prop || null);
  const [data, updateData] = useImmer(initial_data_prop || {});
  const [error, updateError] = useImmer(initial_error || {});
  const [has_changed, setHasChanged] = useState(false);
  const [autosave_status, setAutosaveStatus] = useState("saved"); //"saved", "saving", "error"
  const autosave_timeout = useRef(null);
  const autosave_error_backoff = useRef(1);

  const prev_initial_data_prop = useRef(initial_data_prop);
  const prev_presentation_mode = useRef(presentation_mode);

  if (initial_data_prop !== prev_initial_data_prop.current || presentation_mode !== prev_presentation_mode.current) {
    prev_initial_data_prop.current = initial_data_prop;
    prev_presentation_mode.current = presentation_mode;
    if (presentation_mode) {
      updateData(initial_data_prop || {});
    } else {
      updateData(function(draft) {
        for (let key in initial_data_prop) {
          if (!(key in draft)) {
            draft[key] = initial_data_prop[key];
          }
        }
      });
    }
    setInitialData(initial_data_prop || null);
  }

  const [openBlockerModal] = useLeavePageModal();

  const blocker = useBlocker({ id: unsaved_modal ? "form-"+unsaved_modal : false, beforeunload: true, type: "forms", priority: 3, callback: useCallback(({ proceed }) => {
    openBlockerModal({ leavePage: proceed });
    return true;
  }, [openBlockerModal])});

  useEffect(function() {

    let pretty = !autosave_enabled;

    let pend_has_changed = false;

    for (let field of form_fields.current) {
      if (!compareObjects(field.parseValue(field.formatValue(data[field.name], pretty)), initial_data ? field.parseValue(initial_data[field.name]) : field.parseValue(field.formatValue(field.default_value, pretty)))) {
        pend_has_changed = true;
        break;
        // console.log(field.name)
        // if (field.name == "patient_age") {
          // console.log(field.name)
          // console.log(data[field.name])
          // console.log(field.parseValue(field.formatValue(data[field.name], pretty)))
          // console.log(initial_data ? initial_data[field.name] : field.default_value)
          // console.log(initial_data ? field.parseValue(initial_data[field.name]) : field.parseValue(field.formatValue(field.default_value, pretty)))
          // console.log(field.default_value)
          // console.log(field.parseValue(field.formatValue(field.default_value, pretty)))
        // }
      }
    }

    if (has_changed_prop) {
      pend_has_changed = has_changed_prop;
    }

    setHasChanged(pend_has_changed);

    autosave_error_backoff.current = 1;

    if (pend_has_changed) {
      blocker.block();
    } else {
      blocker.unblock();
    }

  }, [data, initial_data, blocker, autosave_enabled, has_changed_prop]);

  const handleSubmit = useEvent(function({ validate = true, pretty = true, passthrough_args, callback }) {
    let formatted_data = {};
    let errored = false;

    for (let field of form_fields.current) {
      let field_error = (field.disabled || field.readOnly || field.obviated || !validate) ? false : field.validateValue({value: field.parseValue(data[field.name]), required: field.required});
      if (field_error) {
        updateError(draft => {
          draft[field.name] = field_error;
        });
        errored = errored || {field, field_error};
      }
      if (!errored) {
        formatted_data[field.name] = field.formatValue(data[field.name], pretty);
      }
    }

    if (errored) {
      if (onValidateFailed) {
        onValidateFailed({ name: errored.field.name, element: errored.field.getFocusElement({ name: errored.field.name, parent: ref.current, error: errored.field_error }) });
      }
      setWantsFocus({ element: errored.field.getFocusElement({ name: errored.field.name, parent: ref.current, error: errored.field_error }) });
    } else {
      const focusError = function({name, select, error}) {
        updateError(draft => {draft[name] = error})
        setWantsFocus({element: form_fields.current?.find(a => a.name == name)?.getFocusElement({ name, parent: ref.current, error }), select: select});
      }

      let callback_args = {data, formatted_data, updateData, error, updateError, focusError, passthrough_args};

      if (callback) {
        callback(callback_args);
      } else {
        if (onSubmit) {
          onSubmit(callback_args);
        }
      }
    }
  });

  useEffect(function() {
    if (autosave_enabled) {
      if (has_changed) {
        if (autosave_status !== "saving") {
          if (autosave_timeout.current) {
            clearTimeout(autosave_timeout.current);
            autosave_timeout.current = null;
          }
          autosave_timeout.current = setTimeout(() => {
            setAutosaveStatus("saving");

            handleSubmit({
              validate: false,
              pretty: false,
              callback: ({ formatted_data}) => {

                handleAutosave({ formatted_data }).then(function() {
                  setInitialData(formatted_data);
                  setAutosaveStatus("saved");
                  autosave_error_backoff.current = 1;
                }).catch(function() {
                  setAutosaveStatus("error");
                  autosave_error_backoff.current = Math.min(autosave_error_backoff.current * 2, 140); // about 2 minutes
                });

              } 
            });
          }, 800 * autosave_error_backoff.current);
        }
      } else {
        if (autosave_status !== "saving") {
          if (autosave_timeout.current) {
            clearTimeout(autosave_timeout.current);
            autosave_timeout.current = null;
          }
          if (autosave_status == "error") {
            setAutosaveStatus("saved");
          }
        }
      }
    } else {
      if (autosave_timeout.current) {
        clearTimeout(autosave_timeout.current);
        autosave_timeout.current = null;
      }
      if (autosave_status !== "saved") {
        setAutosaveStatus("saved");
      }
    }
  }, [has_changed, autosave_enabled, autosave_status, handleSubmit, handleAutosave, data]);

  const [wants_focus, setWantsFocus] = useState(null);
  useEffect(function() {
    if (wants_focus) {
      if (wants_focus.element) {
        if (wants_focus.select) {
          wants_focus.element.select();
        } else {
          wants_focus.element.focus();
        }
        wants_focus.element.scrollIntoViewIfNeeded();
      }
      setWantsFocus(null);
    }
  }, [wants_focus])

  const registerField = useCallback(({ name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated }) => {
    let find_index = form_fields.current.findIndex(a => a.name == name);
    if (find_index == -1) {
      form_fields.current.push({ name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated });
    } else {
      form_fields.current.splice(find_index, 1, { name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated });
    }
  }, []);

  const formDataContextValue = useMemo(() => ({ data, error }), [data, error]);
  const formCallbacksContextValue = useMemo(() => ({ updateData, updateError, registerField }), [updateData, updateError, registerField]);
  const formGroupContextValue = useMemo(() => ({}), []);
  const formSubmitContextValue = useMemo(() => ({ handleSubmit }), [handleSubmit]);
  const formGlobalContextValue = useMemo(() => ({ has_changed, autosave_status, unblock: blocker.unblock }), [has_changed, autosave_status, blocker.unblock]);
  const formDisabledContextValue = useMemo(() => ({ disabled, readOnly, obviated }), [disabled, readOnly, obviated]);
  const formFilesContextValue = useMemo(() => ({ files }), [files]);

  return (
    <div style={{display: "contents"}} ref={ref}>
      <FormGroupContext.Provider value={formGroupContextValue}>
        <FormDataContext.Provider value={formDataContextValue}>
          <FormCallbacksContext.Provider value={formCallbacksContextValue}>
            <FormSubmitContext.Provider value={formSubmitContextValue}>
              <FormGlobalContext.Provider value={formGlobalContextValue}>
                <FormDisabledContext.Provider value={formDisabledContextValue}>
                  <FormFilesContext.Provider value={formFilesContextValue}>
                    {children}
                  </FormFilesContext.Provider>
                </FormDisabledContext.Provider>
              </FormGlobalContext.Provider>
            </FormSubmitContext.Provider>
          </FormCallbacksContext.Provider>
        </FormDataContext.Provider>
      </FormGroupContext.Provider>
    </div>
  );

}

export function EscapeForm({ children }) {
  return (
    <FormGroupContext.Provider value={undefined}>
      <FormDataContext.Provider value={undefined}>
        <FormCallbacksContext.Provider value={undefined}>
          <FormSubmitContext.Provider value={undefined}>
            <FormGlobalContext.Provider value={undefined}>
              <FormDisabledContext.Provider value={undefined}>
                <FormFilesContext.Provider value={undefined}>
                  {children}
                </FormFilesContext.Provider>
              </FormDisabledContext.Provider>
            </FormGlobalContext.Provider>
          </FormSubmitContext.Provider>
        </FormCallbacksContext.Provider>
      </FormDataContext.Provider>
    </FormGroupContext.Provider>
  )
}

export function FormDisabled({ disabled = false, readOnly = false, obviated = false, indented = false, children }) {

  const form_context = useFormDisabledContext() || {};

  disabled = disabled || form_context.disabled;
  readOnly = readOnly || form_context.readOnly;
  obviated = obviated || form_context.obviated;

  const formDisabledContextValue = useMemo(() => ({ disabled, readOnly, obviated }), [disabled, readOnly, obviated]);

  return (
    <FormDisabledContext.Provider value={formDisabledContextValue}>
      <div className={"form_group" + (obviated ? " form_group_obviated" : "") + (indented ? " form_group_indented" : "")}>
        {children}
      </div>
    </FormDisabledContext.Provider>
  );

}

export function FormObjectGroup({ children, nullify_if_all_empty = false, ...args }) {

  const form_fields = useRef([]);

  const { name, value, updateValue, error, updateError } = useFormField({
    name: args.name,
    validateValue: useCallback(({value}) => {

      let draft = {};
      let errored = false;
      
      for (let field of form_fields.current) {
        let field_error = (field.disabled || field.readOnly || field.obviated) ? false : field.validateValue({value: field.parseValue(value[field.name]), required: field.required});
        if (field_error) {
          draft[field.name] = field_error; // Insert order is preserved
          errored = errored || true;
        }
      }

      if (errored) {
        return draft;
      } else {
        return null;
      }

    }, []),
    formatValue: useCallback((value, pretty) => {

      let formatted_data = {}
      let has_changed_value = false;
      
      for (let field of form_fields.current) {
        formatted_data[field.name] = field.formatValue((value || {})[field.name] == undefined ? field.default_value : (value || {})[field.name], pretty);

        if (nullify_if_all_empty && pretty) {
          if (!compareObjects(field.parseValue(formatted_data[field.name]), field.parseValue(field.formatValue(field.default_value || null, pretty)))) {
            has_changed_value = true;
          }
        }
      }

      if (nullify_if_all_empty && pretty && !has_changed_value) {
        return null;
      }

      return formatted_data;

    }, [nullify_if_all_empty]),
    parseValue: useCallback((value) => value || {}, []),
    getFocusElement: useCallback(({name, parent, error}) => {

      let field = form_fields.current.find(a => a.name == Object.keys(error)[0]);

      return field.getFocusElement({name: name+"."+field.name, parent, error: Object.values(error)[0]});

    }, []),
  });

  const updateData = useCallback((fn) => {
    updateValue(fn);
  }, [updateValue]);

  const updateErrorFn = useCallback((fn) => {
    updateError(currentError => {
      let draft = JSON.parse(JSON.stringify(currentError || {}));
      let return_value = fn(draft);
      return return_value || draft;
    });
  }, [updateError]);

  const registerField = useCallback(({ name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated }) => {
    let find_index = form_fields.current.findIndex(a => a.name == name);
    if (find_index == -1) {
      form_fields.current.push({ name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated });
    } else {
      form_fields.current.splice(find_index, 1, { name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated });
    }
  }, []);
  
  const formGroupContextValue = useMemo(() => ({ name_prefix: name }), [name]);
  const formDataContextValue = useMemo(() => ({ data: value || {}, error: error || {} }), [value, error]);
  const formCallbacksContextValue = useMemo(() => ({ updateData, updateError: updateErrorFn, registerField }), [updateData, updateErrorFn, registerField]);

  return (
    <FormGroupContext.Provider value={formGroupContextValue}>
      <FormDataContext.Provider value={formDataContextValue}>
        <FormCallbacksContext.Provider value={formCallbacksContextValue}>
          {children}
        </FormCallbacksContext.Provider>
      </FormDataContext.Provider>
    </FormGroupContext.Provider>
  );

}

function DefaultFormArrayWrapper({ children }) {
  return (
    <div>
      {children}
    </div>
  );
}

// Could be updated to handle reordering in the future
export function FormArrayGroup({ children, WrapperComponent = DefaultFormArrayWrapper, title, start_with_one, autofocus_first_entry = false, add_button_label, remove_empty_entries = true, handleAddItem, ...args }) {

  const form_fields = useRef([]);
  const [just_added, setJustAdded] = useState(autofocus_first_entry ? "0" : null);

  const { name, value, updateValue, error, updateError, required, disabled, readOnly, obviated } = useFormField({
    name: args.name,
    required: args.required,
    validateValue: useCallback(({value, required}) => {

      if (required && Object.keys(value).length == 0) {
        return required;
      }

      let draft = {};
      let errored = false;
      
      for (let field of form_fields.current) {
        let field_error = (field.disabled || field.readOnly || field.obviated) ? false : field.validateValue({value: field.parseValue(value[field.name]), required: field.required});
        if (field_error) {
          draft[field.name] = field_error; // Insert order is preserved
          errored = errored || true;
        }
      }

      if (errored) {
        return draft;
      } else {
        return null;
      }

    }, []),
    formatValue: useCallback((value, pretty) => {

      let formatted_data = [];
      
      for (let field of form_fields.current) {

        let pend_value = field.formatValue((value || {})[field.name] == undefined ? field.default_value : (value || {})[field.name], pretty);

        if (!remove_empty_entries || pend_value !== null || !pretty) {
          formatted_data.push(pend_value);
        }
      }

      return formatted_data;

    }, [remove_empty_entries]),
    parseValue: useCallback((value) => {
      if (Array.isArray(value)) {
        let draft = {};
        value.forEach((item, index) => {
          draft[index] = item;
        });
        return draft;
      } else {
        return value || {};
      }
    }, []),
    getFocusElement: useCallback(({name, parent, error}) => {

      if (typeof error == "string") {
        return parent.querySelector(`[name="${name}"] .form_array_error_element`);
      } else {
        let field = form_fields.current.find(a => a.name == Object.keys(error)[0]);

        return field.getFocusElement({name: name+"."+field.name, parent, error: Object.values(error)[0]});
      }

    }, []),
    default_value: useMemo(() => start_with_one ? {
      "0": {}
    } : {}, [start_with_one]),
  });

  const updateData = useCallback((fn) => {
    updateValue(fn);
  }, [updateValue]);

  const updateErrorFn = useCallback((fn) => {
    updateError(currentError => {
      let draft = JSON.parse(JSON.stringify(currentError || {}));
      let return_value = fn(draft);
      return return_value || draft;
    });
  }, [updateError]);

  const registerField = useCallback(({ name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated }) => {
    let find_index = form_fields.current.findIndex(a => a.name == name);
    if (find_index == -1) {
      form_fields.current.push({ name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated });
    } else {
      form_fields.current.splice(find_index, 1, { name, validateValue, formatValue, parseValue, default_value, required, getFocusElement, disabled, readOnly, obviated });
    }
  }, []);

  // This internal is accessed directly by handleRemoveDifferential in SessionLayout.jsx
  function handleAdd() {
    if (handleAddItem) {
      handleAddItem(doAddItems);
    } else {
      doAddItems([{}]);
    }

    function doAddItems(items) {
      items.forEach(item => {
        let new_id = String(Date.now());
        updateData(draft => {draft[new_id] = item})
        updateError(draft => {});
        setJustAdded(new_id);
      })
    }
  }

  useEffect(() => {
    if (just_added !== "0") {
      let timeout = setTimeout(() => {
        setJustAdded(null);
      }, 500);
      return () => {
        clearTimeout(timeout);
      }
    }
  }, [just_added])

  useEffect(function() {
    if (just_added && just_added !== "0") {
      requestAnimationFrame(() => {
        let addedItem = document.querySelector("div[name=\""+name+"\"] > *:nth-child("+(title ? 2 : 1)+") > *:nth-child("+(Object.keys(value || {}).findIndex(key => key == just_added) + 1)+")");
        let before_scrolltop = addedItem.closest(".nearest_scrollable_container")?.scrollTop;
        addedItem.scrollIntoViewIfNeeded();
        let after_scrolltop = addedItem.closest(".nearest_scrollable_container")?.scrollTop;
        if (addedItem.closest(".nearest_scrollable_container") && before_scrolltop != after_scrolltop) {
          addedItem.closest(".nearest_scrollable_container").scrollTop += 49 + 16;
        }
      });
    }
  }, [just_added, name, value, title])

  const handleRemove = useCallback((key) => {
    updateData(draft => {delete draft[key]})
    let find_index = form_fields.current.findIndex(a => a.name == key);
    form_fields.current.splice(find_index, 1);
  }, [updateData, form_fields]);

  const formGroupContextValue = useMemo(() => ({ name_prefix: name }), [name]);
  const formDataContextValue = useMemo(() => ({ data: value || {}, error: error || {} }), [value, error]);
  const formCallbacksContextValue = useMemo(() => ({ updateData, updateError: updateErrorFn, registerField }), [updateData, updateErrorFn, registerField]);

  return (
    <FormGroupContext.Provider value={formGroupContextValue}>
      <FormDataContext.Provider value={formDataContextValue}>
        <FormCallbacksContext.Provider value={formCallbacksContextValue}>
          <div name={name}>
            {title ? <label htmlFor={name}>{title}</label> : null}
            <WrapperComponent>
              {Object.keys(value || {}).map((key, index) => (
                <MemoizedArrayItem
                  key={key}
                  itemKey={key}
                  index={index}
                  just_added={just_added == key}
                  obviated={obviated}
                  disabled={disabled}
                  readOnly={readOnly}
                  handleRemove={handleRemove}
                >
                  {children}
                </MemoizedArrayItem>
              ))}
            </WrapperComponent>
            {!readOnly ? (
              <div className="buttons">
                <Button text={add_button_label} icon="plus" disabled={disabled} obviated={obviated} onClick={() => {handleAdd()}}/>
              </div>
            ) : null}
            <div className="form_array_error_element">
              {typeof error == "string" ? <div className="input_error">{error}</div> : null}
            </div>
          </div>
        </FormCallbacksContext.Provider>
      </FormDataContext.Provider>
    </FormGroupContext.Provider>
  );

}

const MemoizedArrayItem = memo(function MemoizedArrayItem({ itemKey, index, just_added, obviated, disabled, readOnly, handleRemove, children }) {
  return (
    <FormObjectGroup name={itemKey} nullify_if_all_empty={true}>
      {children({index, removeItem: () => {handleRemove(itemKey)}, just_added, obviated, disabled, readOnly})}
    </FormObjectGroup>
  );
});

// parseValue is an idempotent function that takes in the db value or the client value and returns the client value. It must be consistent, same input gives the same output. It should also handle undefined to set a default value equivalent to null.
// formatValue takes the client value and returns the db value. Also takes a pretty boolean to indicate if the value is being formatted for final submission or for autosave.
// validateValue is not called if the form field is disabled, obviated, or readOnly. formatValue should always return something.

export const useFormField = ({ name, value, default_value, onChange, error: propError, required, validateValue, formatValue, parseValue, getFocusElement, disabled: propDisabled, readOnly: propReadOnly, obviated: propObviated }) => {
  const fieldData = useContextSelector(FormDataContext, v => v?.data?.[name]);
  const fieldError = useContextSelector(FormDataContext, v => v?.error?.[name]);
  const { updateData, updateError, registerField } = useContext(FormCallbacksContext) || {};
  
  const { disabled, readOnly, obviated } = useFormDisabledContext() || {};
  const { name_prefix } = useFormGroupContext() || {};

  const effective_required = required ? (typeof required == "string" ? required : "This field is required") : null;

  // setValue and updateValue deal with parsed values, since this hook returns parsed values.
  // updateData deals with potentially unparsed values.
  // Thus setValue and updateValue need to never expose unparsed values to the outside world.

  const setValue = useCallback(function(new_value) {
    if (name) {
      updateData(draft => {
        draft[name] = new_value;
      });
    }
    if (onChange) {
      onChange(new_value);
    }
  }, [name, updateData, onChange]);

  const updateValue = useCallback(function(fn) {
    if (name) {
      updateData(draft => {
        if (!draft[name]) {
          draft[name] = JSON.parse(JSON.stringify(parseValue(default_value)));
        } else {
          draft[name] = JSON.parse(JSON.stringify(parseValue(draft[name])));
        }
        let new_value = fn(draft[name]);
        if (new_value !== undefined) {
          draft[name] = new_value;
        }
        if (onChange) { // This could be the source of bugs since it's a side effect inside of an updater callback, which might be triggered when it shouldn't be.
          onChange(draft[name]);
        }
      });
    } else if (onChange) {
      let draft = JSON.parse(JSON.stringify(value || null));
      let return_value = fn(draft);
      onChange(return_value || draft);
    }
  }, [name, updateData, value, parseValue, default_value, onChange]);

  const setError = useCallback(function(new_value) {
    if (name) {
      updateError(draft => {
        draft[name] = new_value;
      });
    }
  }, [name, updateError]);

  const updateErrorFn = useCallback(function(fn) {
    if (name) {
      updateError(draft => {
        draft[name] = fn(draft[name] || null);
      });
    }
  }, [name, updateError]);

  if (name && registerField) { // Check for registerField to avoid issue with hot reload
    registerField({ name, validateValue, formatValue, parseValue, default_value, required: effective_required, getFocusElement, disabled, readOnly, obviated });
  }

  useEffect(function() {
    if (name) {
      if (!(fieldData != null)) {
        updateData(draft => {
          if (!(draft[name] != null)) {
            draft[name] = parseValue(default_value);
          }
        });
      }
    }
  }, [name, !(fieldData != null), default_value]);

  return {
    name: ((name && name_prefix) ? name_prefix+"."+name : name),
    value: ((name ? fieldData : value) != null ? (name ? (parseValue(fieldData)) : value) : parseValue(default_value)),
    setValue,
    updateValue,
    error: ((name ? fieldError : propError) || null),
    setError,
    updateError: updateErrorFn,
    required: effective_required,
    disabled: propDisabled || disabled,
    readOnly: propReadOnly || readOnly,
    obviated: propObviated || obviated
  };
};

export const useFormControl = ({ disabled, readOnly, obviated }) => {
  const form_context = useFormDisabledContext() || {};

  disabled = disabled || form_context.disabled;
  readOnly = readOnly || form_context.readOnly;
  obviated = obviated || form_context.obviated;

  return {
    disabled,
    readOnly,
    obviated
  };
}

export const FormDataProvider = ({children, fields}) => {
  const { disabled, readOnly, obviated } = useFormDisabledContext() || {};
  const { updateData, updateError } = useContext(FormCallbacksContext) || {};

  // If no fields specified, subscribe to everything
  const allData = useContextSelector(FormDataContext, v => fields ? undefined : v?.data);
  const allError = useContextSelector(FormDataContext, v => fields ? undefined : v?.error);
  
  // If fields specified, build selector that returns stable JSON string
  const fieldDataJson = useContextSelector(FormDataContext, useCallback(v => {
    if (!fields) return undefined;
    return JSON.stringify(fields.map(f => v?.data?.[f]));
  }, [fields?.join(',')]));
  
  const fieldErrorJson = useContextSelector(FormDataContext, useCallback(v => {
    if (!fields) return undefined;
    return JSON.stringify(fields.map(f => v?.error?.[f]));
  }, [fields?.join(',')]));

  const data = !fields ? allData : Object.fromEntries(
    fields.map((f, i) => [f, JSON.parse(fieldDataJson)[i]])
  );
  const error = !fields ? allError : Object.fromEntries(
    fields.map((f, i) => [f, JSON.parse(fieldErrorJson)[i]])
  );

  return children({data, error, disabled, readOnly, obviated, updateData, updateError});
}

export const FormSubmitProvider = ({ children }) => {
  const { handleSubmit } = useFormSubmitContext() || {};
  const { has_changed, autosave_status, unblock } = useFormGlobalContext() || {};

  return (
    children({ handleSubmit, has_changed, autosave_status, unblock })
  );
}

export const useFormSubmit = () => {
  const { handleSubmit } = useFormSubmitContext() || {};
  const { has_changed, autosave_status, unblock } = useFormGlobalContext() || {};

  return { handleSubmit, has_changed, autosave_status, unblock };
}

export const useFormData = (fields) => {
  const { updateData, updateError } = useContext(FormCallbacksContext) || {};

  // If no fields specified, subscribe to everything
  const allData = useContextSelector(FormDataContext, v => fields ? undefined : v?.data);
  const allError = useContextSelector(FormDataContext, v => fields ? undefined : v?.error);
  
  // If fields specified, build selector that returns stable JSON string
  const fieldDataJson = useContextSelector(FormDataContext, useCallback(v => {
    if (!fields) return undefined;
    return JSON.stringify(fields.map(f => v?.data?.[f]));
  }, [fields?.join(',')]));
  
  const fieldErrorJson = useContextSelector(FormDataContext, useCallback(v => {
    if (!fields) return undefined;
    return JSON.stringify(fields.map(f => v?.error?.[f]));
  }, [fields?.join(',')]));

  const data = !fields ? allData : Object.fromEntries(
    fields.map((f, i) => [f, JSON.parse(fieldDataJson)[i]])
  );
  const error = !fields ? allError : Object.fromEntries(
    fields.map((f, i) => [f, JSON.parse(fieldErrorJson)[i]])
  );

  return { data, updateData, error, updateError };
}

export const useFormFiles = () => {
  const { files } = useFormFilesContext() || {};

  return { files };
}

export function useLeavePageModal() {
  const [openBlockerModal] = useModal(function ({ context, closeModal }) {

    function leavePage() {
      closeModal();
      context.leavePage();
    }

    return (
      <Modal title="Leave without saving?">
        <ModalContent>
          {"Any changes you haven't saved will be discarded."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="" onClick={closeModal}/>
          <Button className="destructive" text="Leave and discard" onClick={leavePage}/>
        </ModalButtons>
      </Modal>
    )
  })

  return [openBlockerModal];
}